import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from '@/integrations/supabase/client';
import { UserHeader } from './UserHeader';
import { UserSummarySection } from './UserSummarySection';
import { UserContactSection } from './UserContactSection';
import { UserEnrollmentsSection } from './UserEnrollmentsSection';
import { UserCertificatesSection } from './UserCertificatesSection';
import { UserProfileEditDialog } from '../UserProfileEditDialog';
import { UserDeleteDialog } from '../UserDeleteDialog';
import { useUserProfileActions } from './UserProfileActions';

interface UserProfileContainerProps {
  userId: string;
}

export function UserProfileContainer({ userId }: UserProfileContainerProps) {
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);

  // Query user data
  const { data: user, isLoading: isLoadingUser, error: userError } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    staleTime: 0,
  });

  const {
    handleSendResetEmail,
    handleEraseUserData,
    handleDeleteUser,
    deleteDialogOpen,
    setDeleteDialogOpen,
    userName,
  } = useUserProfileActions({
    userId,
    userEmail: user?.email,
    userName: user?.full_name || 'Unknown User',
    onUserDeleted: () => navigate('/users'),
  });

  // Query enrollments
  const { data: enrollments = [], isLoading: isLoadingEnrollments } = useQuery({
    queryKey: ['user-enrollments', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_enrollments')
        .select(`
          id,
          user_id,
          course_id,
          status,
          payment_status,
          progress,
          registered_at,
          last_accessed_at,
          course:course_id (
            course_name,
            course_type,
            price
          )
        `)
        .eq('user_id', userId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
    staleTime: 0,
  });

  if (isLoadingUser) {
    return <div className="p-6 text-center">Loading user profile...</div>;
  }

  if (userError) {
    return (
      <Alert variant="destructive" className="m-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          {userError instanceof Error ? userError.message : 'Failed to load user profile'}
        </AlertDescription>
      </Alert>
    );
  }

  // If no profile exists, show a message but don't prevent viewing auth user data
  if (!user) {
    return (
      <Alert className="m-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Profile Not Created</AlertTitle>
        <AlertDescription>
          This user authenticated but their profile was not created. This typically happens when the user signup trigger failed.
          The profile may need to be created manually or the user should be contacted.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <UserProfileEditDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        user={user}
      />

      <UserDeleteDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        userId={userId}
        userName={userName}
        userEmail={user.email}
        onUserDeleted={() => navigate('/users')}
      />

      <UserHeader
        userName={user.full_name || 'Anonymous User'}
        onEdit={() => setIsEditDialogOpen(true)}
        onSendResetEmail={handleSendResetEmail}
        onEraseData={handleEraseUserData}
        onDelete={handleDeleteUser}
      />

      <UserSummarySection enrollments={enrollments} user={user} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <UserContactSection user={user} />
        <UserEnrollmentsSection 
          enrollments={enrollments} 
          isLoadingEnrollments={isLoadingEnrollments}
        />
      </div>

      <UserCertificatesSection userId={user.id} />
    </div>
  );
}
